import { c as _c } from "react/compiler-runtime";
import type { Iso3166TwoDigitCountryCode, Locale } from '@onefootprint/request-types';
import { cx } from 'class-variance-authority';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { type FieldValues, type Path, type PathValue, type RegisterOptions, useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import mergeRefs from 'react-merge-refs';
import { useInputMask } from '../../../hooks/use-input-mask';
import Select from '../../select';
import TextInput from '../../text-input';
import FormWrapper from '../internals/form-wrapper';
import { extractNationalPart, formatToNational, getCallingCode, getCountriesWithCallingCode, getMask, isPhoneNumberValid, parseDefaultValue, parsePastedNumber, resolveInputValue, toE164 } from '../utils/tel-utils';
import type { FormInputProps } from './form-input';
const countriesWithCallingCode = getCountriesWithCallingCode();
const cursorTrackingConfig = {
  delimiters: ['-', '(', ')']
};
export type FormTelProps<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
  onChange?: (value: string) => void;
  locale?: Locale;
  defaultValue?: string;
} & Omit<FormInputProps<TFieldValues>, 'name' | 'rules' | 'type' | 'autoComplete' | 'onChange' | 'placeholder' | 'defaultValue' | 'onSubmit'>;
export default function FormTel(t0) {
  const $ = _c(47);
  const {
    rules,
    name,
    label,
    hint,
    addon,
    errorPosition: t1,
    orientation: t2,
    size: t3,
    id: baseId,
    onChange,
    locale,
    defaultValue,
    ...props
  } = t0;
  const errorPosition = t1 === undefined ? "relative" : t1;
  const orientation = t2 === undefined ? "vertical" : t2;
  const size = t3 === undefined ? "default" : t3;
  const {
    t
  } = useTranslation("ui");
  const {
    register,
    setValue,
    getValues,
    control,
    getFieldState
  } = useFormContext();
  const formState = useFormState({
    control,
    name,
    disabled: props.disabled
  });
  let t4;
  if ($[0] !== formState || $[1] !== getFieldState || $[2] !== name) {
    t4 = getFieldState(name, formState);
    $[0] = formState;
    $[1] = getFieldState;
    $[2] = name;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const {
    error
  } = t4;
  const telInputRef = useRef(null);
  const lastE164Ref = useRef("");
  let t5;
  if ($[4] !== getValues || $[5] !== name) {
    t5 = getValues(name);
    $[4] = getValues;
    $[5] = name;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const rhfDefaultValue = t5;
  const t6 = defaultValue || rhfDefaultValue;
  let t7;
  if ($[7] !== locale || $[8] !== t6) {
    t7 = parseDefaultValue(t6, locale);
    $[7] = locale;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const parsedDefaultValue = t7;
  const contextId = useId();
  const [country, setCountry] = useState(parsedDefaultValue?.country ?? "US");
  const [isFocused, setIsFocused] = useState(false);
  const callingCode = getCallingCode(country) || "";
  const id = baseId || contextId;
  const {
    inputRef
  } = useInputMask(cursorTrackingConfig);
  let t8;
  if ($[10] !== country) {
    t8 = getMask(country);
    $[10] = country;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const placeholder = t8;
  let t9;
  bb0: {
    if (!parsedDefaultValue?.value) {
      t9 = "";
      break bb0;
    }
    let t10;
    if ($[12] !== parsedDefaultValue.country || $[13] !== parsedDefaultValue.value) {
      t10 = extractNationalPart(parsedDefaultValue.value, parsedDefaultValue.country);
      $[12] = parsedDefaultValue.country;
      $[13] = parsedDefaultValue.value;
      $[14] = t10;
    } else {
      t10 = $[14];
    }
    t9 = t10;
  }
  const nationalDefaultValue = t9;
  let t10;
  if ($[15] !== country || $[16] !== name || $[17] !== setValue) {
    t10 = e => {
      e.preventDefault();
      const nationalNumber = parsePastedNumber(e.clipboardData.getData("text"), country);
      setValue(name, nationalNumber as PathValue<TFieldValues, Path<TFieldValues>>, {
        shouldDirty: true,
        shouldValidate: true
      });
    };
    $[15] = country;
    $[16] = name;
    $[17] = setValue;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const handlePaste = t10;
  let t11;
  if ($[19] !== country || $[20] !== t) {
    t11 = value => {
      if (isPhoneNumberValid(value, country)) {
        return true;
      }
      return t("components.form.tel.errors.invalid");
    };
    $[19] = country;
    $[20] = t;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const telRules = {
    validate: t11,
    ...rules
  };
  const {
    ref: rhfRef,
    ...registerProps
  } = register(name, {
    ...telRules,
    disabled: formState.disabled || props.disabled,
    onChange: t12 => {
      const {
        target: t13
      } = t12;
      const {
        value: value_0
      } = t13;
      const e164Value = toE164(value_0, country, callingCode);
      lastE164Ref.current = e164Value;
      const formattedNational = formatToNational(e164Value, country, callingCode) as PathValue<TFieldValues, Path<TFieldValues>>;
      setValue(name, formattedNational);
    },
    setValueAs: value_1 => {
      if (!value_1?.trim()) {
        return "";
      }
      if (lastE164Ref.current) {
        const result = lastE164Ref.current;
        lastE164Ref.current = "";
        onChange?.(result);
        return result;
      }
      const e164Value_0 = toE164(value_1, country, callingCode);
      onChange?.(e164Value_0);
      return e164Value_0;
    }
  });
  let t14;
  if ($[22] !== locale) {
    t14 = [locale];
    $[22] = locale;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  useEffect(() => {
    if (telInputRef.current?.value) {
      const {
        nationalValue,
        country: resolvedCountry
      } = resolveInputValue(telInputRef.current.value, locale, callingCode);
      telInputRef.current.value = nationalValue;
      setCountry(resolvedCountry);
    }
  }, t14);
  const t15 = !isFocused;
  let t16;
  if ($[24] !== isFocused || $[25] !== t15) {
    t16 = cx("[&>button]:rounded-b-none!", {
      "[&>button]:border-b!": isFocused,
      "[&>button]:border-b-0!": t15
    });
    $[24] = isFocused;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  let t18;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => {
      setIsFocused(true);
    };
    t18 = () => {
      setIsFocused(false);
    };
    $[27] = t17;
    $[28] = t18;
  } else {
    t17 = $[27];
    t18 = $[28];
  }
  let t19;
  if ($[29] !== name || $[30] !== setValue) {
    t19 = value_2 => {
      const countryCode = value_2 as Iso3166TwoDigitCountryCode;
      setCountry(countryCode);
      setValue(name, "" as PathValue<TFieldValues, Path<TFieldValues>>, {
        shouldDirty: true
      });
    };
    $[29] = name;
    $[30] = setValue;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  const T0 = TextInput;
  const t20 = mergeRefs([telInputRef, rhfRef, inputRef]);
  const t21 = "tel";
  const t22 = "tel";
  const t23 = !!error;
  const t24 = "mask";
  const t25 = `calc(${callingCode.length}ch + 1.25rem)`;
  let t26;
  if ($[32] !== t25) {
    t26 = {
      paddingLeft: t25
    };
    $[32] = t25;
    $[33] = t26;
  } else {
    t26 = $[33];
  }
  const t27 = cx("rounded-t-none!", {
    "rounded-bl-none! rounded-tl-none!": addon,
    "border-t-0": isFocused
  }, props.className);
  let t28;
  if ($[34] !== T0 || $[35] !== handlePaste || $[36] !== id || $[37] !== nationalDefaultValue || $[38] !== placeholder || $[39] !== props || $[40] !== registerProps || $[41] !== size || $[42] !== t20 || $[43] !== t23 || $[44] !== t26 || $[45] !== t27) {
    t28 = <T0 {...props} {...registerProps} ref={t20} onPaste={handlePaste} defaultValue={nationalDefaultValue} type={t21} autoComplete={t22} placeholder={placeholder} hasError={t23} id={id} size={size} data-dd-privacy={t24} style={t26} className={t27} />;
    $[34] = T0;
    $[35] = handlePaste;
    $[36] = id;
    $[37] = nationalDefaultValue;
    $[38] = placeholder;
    $[39] = props;
    $[40] = registerProps;
    $[41] = size;
    $[42] = t20;
    $[43] = t23;
    $[44] = t26;
    $[45] = t27;
    $[46] = t28;
  } else {
    t28 = $[46];
  }
  return <div className="flex flex-col min-w-[300px]"><FormWrapper id={id} error={error?.message} label={label} errorPosition={errorPosition} size={size} hint={hint} addon={addon} orientation={orientation} fullWidth={true} disabled={formState.disabled || props.disabled}><div><Select disabled={formState.disabled || props.disabled} options={countriesWithCallingCode} className={t16} onFocus={t17} onBlur={t18} onChange={t19} value={country} /><div className="relative isolate"><span className={cx("absolute left-3 inset-y-0 flex items-center pointer-events-none select-none z-10 text-sm text-primary", {
            "text-tertiary": formState.disabled || props.disabled
          })} aria-hidden="true">{callingCode}</span>{t28}</div></div></FormWrapper></div>;
}